<script setup lang="ts">
import { useViteEnvironment } from '@/shared/composables/useViteEnvironment'
import {
  adminInspectPersonaCsv,
  useAdminGetPersonaImportOptions,
} from '@swipe4work/api-orval'
import type { PersonaImportInspectResult } from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { useLocalStorage } from '@vueuse/core'
import { useToast } from 'primevue/usetoast'
import PersonaImportRunDialog from '@/features/settings/dialogs/PersonaImportRunDialog.vue'
import {
  emptyPersistedMapping,
  emptyValueMaps,
  PERSONA_FIELD_LABELS,
  REQUIRED_PERSONA_FIELDS,
  type DistributionOption,
  type HoursRange,
  type PersistedPersonaImportMapping,
  type PersonaImportColumns,
  type PersonaImportMapping,
  type PersonaImportValueMaps,
  type PersonaUniqueMatchField,
  type YearsRange,
} from '@/features/settings/types/personaImport'

definePage({
  path: '/settings/personas',
  name: '/settings/PersonasImport',
  meta: {
    title: 'Personas importeren',
    permission: {
      resource: 'PersonaImport',
      level: 'Read',
    },
  },
})

const { isProd } = useViteEnvironment()
const toast = useToast()

/*
 * ── Persisted state ──────────────────────────────────────────────────────
 * Columns, defaults, uniqueMatchField and yearsExperienceValueMap are persisted.
 * Other value maps (hardSkill/certificate/jobFunction) live in a separate ref
 * and reset per CSV — writing them to localStorage on every keystroke caused UI
 * lag on large inputs.
 */
const mapping = useLocalStorage<PersistedPersonaImportMapping>(
  'persona-import-mapping-v5',
  emptyPersistedMapping(),
  { mergeDefaults: true },
)
const learnedHeaderToField = useLocalStorage<Record<string, keyof PersonaImportColumns>>(
  'persona-import-header-mappings-v1',
  {},
  { mergeDefaults: true },
)

// Non-persistent: rebuilt per upload, ignored by localStorage.
const valueMaps = ref<PersonaImportValueMaps>(emptyValueMaps())
// CSV values the admin explicitly chose to drop instead of mapping.
const skippedHardSkills = ref<Set<string>>(new Set())
const skippedCertificates = ref<Set<string>>(new Set())
const skippedJobFunctions = ref<Set<string>>(new Set())

// ── State ────────────────────────────────────────────────────────────────
const csvFile = ref<File | null>(null)
const inspect = ref<PersonaImportInspectResult | null>(null)
const isInspecting = ref(false)
const dialog = useDynamicDialog()

const { data: options, isPending: isLoadingOptions } = useAdminGetPersonaImportOptions(
  { query: { enabled: !isProd } },
)

const headerOptions = computed(() => {
  const headers = inspect.value?.headers ?? []

  return [
    {
      label: '— Niet gemapt —',
      value: null,
    },
    ...headers.map(h => ({
      label: h,
      value: h,
    })),
  ]
})

const uniqueFieldOptions: {
  label: string,
  value: PersonaUniqueMatchField
}[] = [
  {
    label: 'Email',
    value: 'Email',
  },
  {
    label: 'Firebase ExternalId',
    value: 'ExternalId',
  },
  {
    label: 'Database Id',
    value: 'Id',
  },
]

const PRESET_HOURS: HoursRange[] = [
  {
    min: 8,
    max: 16,
  },
  {
    min: 16,
    max: 24,
  },
  {
    min: 24,
    max: 32,
  },
  {
    min: 32,
    max: 40,
  },
  {
    min: 38,
    max: 40,
  },
]

const PRESET_YEARS: YearsRange[] = [
  {
    min: 0,
    max: 1,
  },
  {
    min: 1,
    max: 2,
  },
  {
    min: 2,
    max: 4,
  },
  {
    min: 4,
    max: 7,
  },
  {
    min: 7,
    max: 10,
  },
  {
    min: 10,
    max: 15,
  },
  {
    min: 15,
    max: 99,
  },
]

// ── Lookups for DB reference data ────────────────────────────────────────
const dbHardSkillNames = computed(() => options.value?.hardSkills ?? [])
const dbCertificateNames = computed(() => options.value?.certificates ?? [])
const dbJobFunctionNames = computed(() => options.value?.jobFunctions ?? [])

function normalizeValues(values: readonly string[]): Record<string, string> {
  const result: Record<string, string> = {}

  for (const raw of values) {
    const trimmed = raw.trim()
    const normalized = trimmed.toLowerCase()

    if (normalized && result[normalized] === undefined) {
      result[normalized] = trimmed
    }
  }

  return result
}

// ── Years experience value mapping ───────────────────────────────────────
function rangeKey(range: YearsRange): string {
  return `${range.min}-${range.max}`
}

const yearPresetOptions = PRESET_YEARS.map(r => ({
  label: `${rangeLabel(r)} jaar`,
  value: rangeKey(r),
}))

/** Mirrors the C# TryParseRange: only accepts pure integers optionally joined by a dash. */
function canAutoParseYears(cell: string): boolean {
  const trimmed = cell.trim()

  return /^\d+[-–]\d+$/.test(trimmed) || /^\d+$/.test(trimmed)
}

const csvYearsValues = computed<string[]>(() => {
  const col = mapping.value.columns.yearsExperience
  if (!col || !inspect.value) return []

  return inspect.value.distinctValuesByHeader?.[col] ?? []
})

/** Values that the backend can't auto-parse and haven't been mapped yet. */
const unmappedYearsValues = computed(() =>
  csvYearsValues.value.filter(v => !canAutoParseYears(v)),
)

function getYearsMappingKey(csvValue: string): string | null {
  const range = mapping.value.yearsExperienceValueMap[csvValue]
  if (!range) return null

  return rangeKey(range)
}

function setYearsMapping(csvValue: string, key: string | null) {
  if (!key) {
    const next = { ...mapping.value.yearsExperienceValueMap }

    delete next[csvValue]
    mapping.value.yearsExperienceValueMap = next

    return
  }
  const range = PRESET_YEARS.find(r => rangeKey(r) === key)
  if (!range) return
  mapping.value.yearsExperienceValueMap = {
    ...mapping.value.yearsExperienceValueMap,
    [csvValue]: range,
  }
}

// ── Per-value mapping helpers (skills / certificates / functions) ─────────
function distinctSplitValues(...columns: (string | null)[]): string[] {
  if (!inspect.value) return []
  const out = new Set<string>()

  for (const col of columns) {
    if (!col) continue
    const distinct = inspect.value.distinctValuesByHeader?.[col] ?? []

    for (const cell of distinct) {
      for (const part of cell.split(/[,;]+/).map(s => s.trim()).filter(Boolean)) {
        out.add(part)
      }
    }
  }

  return [ ...out ].sort((a, b) => a.localeCompare(b))
}

function missingAgainst(values: string[], dbNames: string[]): string[] {
  const dbSet = new Set(Object.keys(normalizeValues(dbNames)))

  return values.filter(v => !dbSet.has(v.trim().toLowerCase()))
}

const missingHardSkills = computed(() =>
  missingAgainst(
    distinctSplitValues(mapping.value.columns.hardSkills, mapping.value.columns.topSkills),
    dbHardSkillNames.value,
  ),
)
const missingCertificates = computed(() =>
  missingAgainst(distinctSplitValues(mapping.value.columns.certificates), dbCertificateNames.value),
)
const missingJobFunctions = computed(() =>
  missingAgainst(distinctSplitValues(mapping.value.columns.jobFunctions), dbJobFunctionNames.value),
)

function getValueMap(map: Record<string, string[]>, csvValue: string): string[] {
  return map[csvValue] ?? []
}

function setValueMap(
  field: 'hardSkillValueMap' | 'certificateValueMap' | 'jobFunctionValueMap',
  csvValue: string,
  dbValues: string[],
) {
  valueMaps.value[field] = {
    ...valueMaps.value[field],
    [csvValue]: dbValues,
  }
}

// ── Skip toggles ─────────────────────────────────────────────────────────
function isHardSkillSkipped(csvValue: string): boolean {
  return skippedHardSkills.value.has(csvValue)
}

function setHardSkillSkip(csvValue: string, skip: boolean) {
  const next = new Set(skippedHardSkills.value)
  if (skip) {
    next.add(csvValue)
    const nextMap = { ...valueMaps.value.hardSkillValueMap }

    delete nextMap[csvValue]
    valueMaps.value.hardSkillValueMap = nextMap
  }
  else {
    next.delete(csvValue)
  }
  skippedHardSkills.value = next
}

function isCertificateSkipped(csvValue: string): boolean {
  return skippedCertificates.value.has(csvValue)
}

function setCertificateSkip(csvValue: string, skip: boolean) {
  const next = new Set(skippedCertificates.value)
  if (skip) {
    next.add(csvValue)
    const nextMap = { ...valueMaps.value.certificateValueMap }

    delete nextMap[csvValue]
    valueMaps.value.certificateValueMap = nextMap
  }
  else {
    next.delete(csvValue)
  }
  skippedCertificates.value = next
}

function isJobFunctionSkipped(csvValue: string): boolean {
  return skippedJobFunctions.value.has(csvValue)
}

function setJobFunctionSkip(csvValue: string, skip: boolean) {
  const next = new Set(skippedJobFunctions.value)
  if (skip) {
    next.add(csvValue)
    const nextMap = { ...valueMaps.value.jobFunctionValueMap }

    delete nextMap[csvValue]
    valueMaps.value.jobFunctionValueMap = nextMap
  }
  else {
    next.delete(csvValue)
  }
  skippedJobFunctions.value = next
}

function skipAllMissing() {
  for (const csv of missingHardSkills.value) {
    if (!isHardSkillSkipped(csv) && (valueMaps.value.hardSkillValueMap[csv]?.length ?? 0) === 0) {
      setHardSkillSkip(csv, true)
    }
  }
  for (const csv of missingCertificates.value) {
    if (!isCertificateSkipped(csv) && (valueMaps.value.certificateValueMap[csv]?.length ?? 0) === 0) {
      setCertificateSkip(csv, true)
    }
  }
  for (const csv of missingJobFunctions.value) {
    if (!isJobFunctionSkipped(csv) && (valueMaps.value.jobFunctionValueMap[csv]?.length ?? 0) === 0) {
      setJobFunctionSkip(csv, true)
    }
  }
}

// ── Validation ───────────────────────────────────────────────────────────
const missingRequiredFields = computed(() =>
  REQUIRED_PERSONA_FIELDS.filter(f => !mapping.value.columns[f]),
)

const validationErrors = computed<string[]>(() => {
  const errors: string[] = []

  for (const field of missingRequiredFields.value) {
    errors.push(`Verplichte kolom-mapping ontbreekt: ${PERSONA_FIELD_LABELS[field]}.`)
  }

  // Either column-mapped OR at least one default option selected.
  if (!mapping.value.columns.city && mapping.value.defaults.cities.length === 0) {
    errors.push('Map de kolom "Stad" óf selecteer minstens één default stad.')
  }
  if (!mapping.value.columns.educationLevel && mapping.value.defaults.educationLevels.length === 0) {
    errors.push('Map de kolom "Opleidingsniveau" óf selecteer minstens één default opleidingsniveau.')
  }
  if (!mapping.value.columns.hoursPerWeek && mapping.value.defaults.hoursPerWeek.length === 0) {
    errors.push('Map de kolom "Uren per week" óf selecteer minstens één default uren-range.')
  }
  if (!mapping.value.columns.yearsExperience && mapping.value.defaults.yearsExperience.length === 0) {
    errors.push('Map de kolom "Werkervaring" óf selecteer minstens één default jaren-range.')
  }
  if (mapping.value.defaults.maxDistanceKm < 1) {
    errors.push('Max afstand (km) moet groter dan 0 zijn.')
  }
  if (mapping.value.defaults.minAge < 16 || mapping.value.defaults.maxAge < mapping.value.defaults.minAge) {
    errors.push('Stel een geldige leeftijdsrange in (min ≥ 16, max ≥ min).')
  }

  // Unparseable years values must either be mapped OR defaults must be set as fallback.
  if (mapping.value.columns.yearsExperience && mapping.value.defaults.yearsExperience.length === 0) {
    for (const csv of unmappedYearsValues.value) {
      if (!mapping.value.yearsExperienceValueMap[csv]) {
        errors.push(`Werkervaring "${csv}" kan niet worden herkend en heeft geen default-range.`)
      }
    }
  }

  // Missing hard skills / certificates / functions — admin must map or explicitly skip.
  for (const csv of missingHardSkills.value) {
    if (skippedHardSkills.value.has(csv)) continue
    if ((valueMaps.value.hardSkillValueMap[csv]?.length ?? 0) === 0) {
      errors.push(`Hard skill / top skill "${csv}" bestaat niet in de database en is niet gemapt.`)
    }
  }
  for (const csv of missingCertificates.value) {
    if (skippedCertificates.value.has(csv)) continue
    if ((valueMaps.value.certificateValueMap[csv]?.length ?? 0) === 0) {
      errors.push(`Certificaat "${csv}" bestaat niet in de database en is niet gemapt.`)
    }
  }
  for (const csv of missingJobFunctions.value) {
    if (skippedJobFunctions.value.has(csv)) continue
    if ((valueMaps.value.jobFunctionValueMap[csv]?.length ?? 0) === 0) {
      errors.push(`Functie "${csv}" bestaat niet in de database en is niet gemapt.`)
    }
  }

  return errors
})

const canImport = computed(() =>
  !!inspect.value
  && validationErrors.value.length === 0
  && !isInspecting.value,
)

/*
 * ── Header → field memory ────────────────────────────────────────────────
 * When a CSV is inspected, rebuild the column mappings by looking up each header
 * in the learned `header → field` map. This lets the admin re-upload any CSV
 * and keep the previously chosen mapping for headers they've seen before.
 */
watch(inspect, (next) => {
  if (!next) return
  const rebuilt = { ...emptyPersistedMapping().columns }

  for (const header of next.headers) {
    const field = learnedHeaderToField.value[header]
    if (field && field in rebuilt) {
      rebuilt[field] = header
    }
  }
  mapping.value.columns = rebuilt
})

/*
 * When the admin sets/changes a column mapping, remember that header → field
 * assignment so future CSVs benefit from it. Clearing a mapping does NOT forget
 * the learned mapping — it stays until a different field claims that header.
 */
watch(() => mapping.value.columns, (cols) => {
  const next = { ...learnedHeaderToField.value }

  for (const [ field, header ] of Object.entries(cols)) {
    if (header) {
      next[header] = field as keyof PersonaImportColumns
    }
  }
  learnedHeaderToField.value = next
}, { deep: true })

// ── File handling ────────────────────────────────────────────────────────
async function handleFileSelect(event: { files: File | File[] }) {
  const file = Array.isArray(event.files) ? event.files[0] : event.files
  if (!file) return
  csvFile.value = file

  isInspecting.value = true
  try {
    inspect.value = await adminInspectPersonaCsv({ Csv: file })
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'CSV inlezen mislukt',
      detail: error instanceof Error ? error.message : 'Onbekende fout.',
      life: 5000,
    })
    csvFile.value = null
    inspect.value = null
  }
  finally {
    isInspecting.value = false
  }
}

function clearFile() {
  csvFile.value = null
  inspect.value = null
}

// ── Distribution helpers ─────────────────────────────────────────────────
function totalWeight<T>(items: DistributionOption<T>[]) {
  return items.reduce((acc, item) => acc + Math.max(0, item.weight), 0)
}

function percentage<T>(items: DistributionOption<T>[], item: DistributionOption<T>) {
  const total = totalWeight(items)
  if (total <= 0) return 0

  return Math.round((Math.max(0, item.weight) / total) * 100)
}

function setDistribution<T>(
  items: DistributionOption<T>[],
  selected: T[],
  isEqual: (a: T, b: T) => boolean,
): DistributionOption<T>[] {
  const next: DistributionOption<T>[] = []

  for (const value of selected) {
    const existing = items.find(i => isEqual(i.value, value))

    next.push(existing ?? {
      value,
      weight: 1,
    })
  }

  return next
}

const stringEq = (a: string, b: string) => a === b
const hoursEq = (a: HoursRange, b: HoursRange) => a.min === b.min && a.max === b.max
const yearsEq = (a: YearsRange, b: YearsRange) => a.min === b.min && a.max === b.max

function rangeLabel(range: {
  min: number,
  max: number
}) {
  if (range.max >= 99) return `${range.min}+`

  return range.min === range.max ? `${range.min}` : `${range.min}-${range.max}`
}

// ── Import ───────────────────────────────────────────────────────────────
function buildFullMapping(): PersonaImportMapping {
  /*
   * Deep-clone reactive maps to plain objects so any Vue proxy weirdness is
   * eliminated before JSON.stringify runs in the dialog.
   */
  const cleanValueMaps: PersonaImportValueMaps = JSON.parse(JSON.stringify(valueMaps.value))

  /*
   * Skipped values map to an empty array — the backend's ApplyValueMap drops
   * them, so the persona's import simply doesn't get that skill/certificate/function.
   */
  const hardSkillValueMap = { ...cleanValueMaps.hardSkillValueMap }

  for (const csv of skippedHardSkills.value) {
    hardSkillValueMap[csv] = []
  }

  const certificateValueMap = { ...cleanValueMaps.certificateValueMap }

  for (const csv of skippedCertificates.value) {
    certificateValueMap[csv] = []
  }

  const jobFunctionValueMap = { ...cleanValueMaps.jobFunctionValueMap }

  for (const csv of skippedJobFunctions.value) {
    jobFunctionValueMap[csv] = []
  }

  return {
    columns: JSON.parse(JSON.stringify(mapping.value.columns)),
    defaults: JSON.parse(JSON.stringify(mapping.value.defaults)),
    uniqueMatchField: mapping.value.uniqueMatchField,
    yearsExperienceValueMap: JSON.parse(JSON.stringify(mapping.value.yearsExperienceValueMap)),
    hardSkillValueMap,
    certificateValueMap,
    jobFunctionValueMap,
  }
}

function submitImport() {
  if (!csvFile.value || !inspect.value) return

  dialog.open(PersonaImportRunDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: false,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: {
      csv: csvFile.value,
      mapping: buildFullMapping(),
      totalRows: inspect.value.totalRows,
    },
  })
}

const columnFields = computed<(keyof PersonaImportColumns)[]>(() => [
  'firstName',
  'lastName',
  'email',
  'externalId',
  'id',
  'jobFunctions',
  'taskDescription',
  'hardSkills',
  'topSkills',
  'certificates',
  'city',
  'educationLevel',
  'hoursPerWeek',
  'yearsExperience',
  'birthDate',
])

function isRequired(field: keyof PersonaImportColumns) {
  return REQUIRED_PERSONA_FIELDS.includes(field)
}
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <Message v-if="isProd" severity="warn" :closable="false">
      Persona import is uitgeschakeld in productie.
    </Message>

    <template v-else>
      <!-- ── Step 1: Upload CSV ─────────────────────────────────────────── -->
      <Card>
        <template #title>
          <span>1. CSV-bestand uploaden</span>
        </template>
        <template #content>
          <div v-if="!inspect" class="flex flex-col gap-3">
            <FileUpload
              mode="basic"
              :auto="true"
              accept=".csv,text/csv"
              :custom-upload="true"
              choose-label="Selecteer CSV"
              :disabled="isInspecting"
              @uploader="handleFileSelect"
            />
            <span class="text-sm text-surface-500">
              CSV met komma als delimiter. Multi-value cellen (functies, skills, certificaten)
              worden gesplitst op komma of puntkomma binnen de cel.
            </span>
          </div>

          <div v-else class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <i class="fa-regular fa-file-csv text-primary-500" />
                <span class="font-semibold">{{ csvFile?.name }}</span>
                <Tag :value="`${inspect.totalRows} rijen`" severity="info" />
              </div>
              <Button
                label="Ander bestand"
                icon="fa-regular fa-xmark"
                severity="secondary"
                size="small"
                @click="clearFile"
              />
            </div>

            <div class="overflow-x-auto">
              <DataTable :value="inspect.sampleRows" size="small" :show-gridlines="true">
                <Column
                  v-for="(header, idx) in inspect.headers"
                  :key="header"
                  :header="header"
                  :field="String(idx)"
                  header-class="text-xs"
                >
                  <template #body="{ data }">
                    <span class="line-clamp-2 text-xs">{{ data[idx] }}</span>
                  </template>
                </Column>
              </DataTable>
              <span class="mt-1 block text-xs text-surface-500">
                Eerste {{ inspect.sampleRows.length }} rijen ter referentie.
              </span>
            </div>
          </div>
        </template>
      </Card>

      <!-- ── Step 2: Mapping & defaults ─────────────────────────────────── -->
      <template v-if="inspect">
        <Card>
          <template #title>
            <span>2. Kolommen mappen</span>
          </template>
          <template #content>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div
                v-for="field in columnFields"
                :key="field"
                class="flex flex-col gap-1"
              >
                <label :for="`map-${field}`" class="text-sm font-semibold">
                  {{ PERSONA_FIELD_LABELS[field] }}
                  <span v-if="isRequired(field)" class="text-red-600">*</span>
                </label>
                <Select
                  :input-id="`map-${field}`"
                  v-model="mapping.columns[field]"
                  :options="headerOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Kies kolom..."
                  :show-clear="!isRequired(field)"
                />
              </div>
            </div>

            <Message
              v-if="missingRequiredFields.length"
              severity="warn"
              class="mt-4"
              :closable="false"
            >
              Verplichte mapping ontbreekt: {{
                missingRequiredFields.map(f => PERSONA_FIELD_LABELS[f]).join(', ')
              }}.
            </Message>
          </template>
        </Card>

        <!-- ── Step 2b: Werkervaring-waarden mappen ───────────────────────── -->
        <Card v-if="mapping.columns.yearsExperience && unmappedYearsValues.length">
          <template #title>
            <span>2b. Werkervaring-waarden mappen</span>
          </template>
          <template #content>
            <div class="flex flex-col gap-3">
              <p class="m-0 text-sm text-surface-500">
                De onderstaande waarden uit kolom <strong>{{ mapping.columns.yearsExperience }}</strong> kunnen
                niet automatisch worden herkend als een jaren-range. Selecteer voor elke waarde de
                bijbehorende range.
              </p>
              <div
                v-for="csvValue in unmappedYearsValues"
                :key="csvValue"
                class="flex flex-col gap-2 border border-surface-200 rounded-lg p-3 md:flex-row md:items-center md:gap-4"
              >
                <div class="flex flex-1 items-center gap-2">
                  <Tag
                    v-if="mapping.yearsExperienceValueMap[csvValue]"
                    value="gemapt"
                    severity="info"
                  />
                  <Tag
                    v-else
                    value="onbekend"
                    severity="warn"
                  />
                  <span class="break-all text-sm font-mono">{{ csvValue }}</span>
                </div>
                <Select
                  :model-value="getYearsMappingKey(csvValue)"
                  :options="yearPresetOptions"
                  option-label="label"
                  option-value="value"
                  show-clear
                  placeholder="Selecteer jaren-range"
                  style="min-width: 14rem"
                  @update:model-value="(v) => setYearsMapping(csvValue, v as string | null)"
                />
              </div>
            </div>
          </template>
        </Card>

        <!-- ── Step 2c: Ontbrekende skills / certificaten / functies ───── -->
        <Card
          v-if="missingHardSkills.length || missingCertificates.length || missingJobFunctions.length"
        >
          <template #title>
            <div class="flex items-center justify-between">
              <span>2c. Ontbrekende waarden mappen</span>
              <Button
                label="Sla alles over"
                icon="fa-regular fa-ban"
                severity="secondary"
                size="small"
                @click="skipAllMissing"
              />
            </div>
          </template>
          <template #content>
            <div class="flex flex-col gap-6">
              <p class="m-0 text-sm text-surface-500">
                De volgende waarden uit de CSV bestaan nog niet in de database. Map ze naar
                bestaande items om ze tijdens import te vervangen — of sla ze over om ze weg te laten.
              </p>

              <div v-if="missingJobFunctions.length" class="flex flex-col gap-2">
                <h3 class="m-0 text-sm font-semibold">
                  Functies ({{ missingJobFunctions.length }})
                </h3>
                <div
                  v-for="csvValue in missingJobFunctions"
                  :key="`fn-${csvValue}`"
                  class="flex flex-col gap-2 border border-surface-200 rounded-lg p-3"
                >
                  <div class="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
                    <div class="flex flex-1 items-center gap-2">
                      <Tag
                        v-if="isJobFunctionSkipped(csvValue)"
                        value="overgeslagen"
                        severity="secondary"
                      />
                      <Tag
                        v-else
                        :value="(valueMaps.jobFunctionValueMap[csvValue]?.length ?? 0) > 0 ? 'gemapt' : 'ontbreekt'"
                        :severity="(valueMaps.jobFunctionValueMap[csvValue]?.length ?? 0) > 0 ? 'info' : 'warn'"
                      />
                      <span class="break-all text-sm font-mono">{{ csvValue }}</span>
                    </div>
                    <MultiSelect
                      :model-value="getValueMap(valueMaps.jobFunctionValueMap, csvValue)"
                      :options="dbJobFunctionNames"
                      :loading="isLoadingOptions"
                      :disabled="isJobFunctionSkipped(csvValue)"
                      display="chip"
                      filter
                      placeholder="Selecteer functie(s)"
                      style="min-width: 20rem"
                      @update:model-value="(v) => setValueMap('jobFunctionValueMap', csvValue, v as string[])"
                    />
                  </div>
                  <div class="flex items-center gap-2 pl-1">
                    <Checkbox
                      :input-id="`skip-fn-${csvValue}`"
                      :model-value="isJobFunctionSkipped(csvValue)"
                      binary
                      @update:model-value="(v) => setJobFunctionSkip(csvValue, !!v)"
                    />
                    <label :for="`skip-fn-${csvValue}`" class="text-xs text-surface-500">
                      Niet importeren — laat deze waarde weg bij elke persona
                    </label>
                  </div>
                </div>
              </div>

              <div v-if="missingHardSkills.length" class="flex flex-col gap-2">
                <h3 class="m-0 text-sm font-semibold">
                  Hard / top skills ({{ missingHardSkills.length }})
                </h3>
                <div
                  v-for="csvValue in missingHardSkills"
                  :key="`hs-${csvValue}`"
                  class="flex flex-col gap-2 border border-surface-200 rounded-lg p-3"
                >
                  <div class="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
                    <div class="flex flex-1 items-center gap-2">
                      <Tag
                        v-if="isHardSkillSkipped(csvValue)"
                        value="overgeslagen"
                        severity="secondary"
                      />
                      <Tag
                        v-else
                        :value="(valueMaps.hardSkillValueMap[csvValue]?.length ?? 0) > 0 ? 'gemapt' : 'ontbreekt'"
                        :severity="(valueMaps.hardSkillValueMap[csvValue]?.length ?? 0) > 0 ? 'info' : 'warn'"
                      />
                      <span class="break-all text-sm font-mono">{{ csvValue }}</span>
                    </div>
                    <MultiSelect
                      :model-value="getValueMap(valueMaps.hardSkillValueMap, csvValue)"
                      :options="dbHardSkillNames"
                      :loading="isLoadingOptions"
                      :disabled="isHardSkillSkipped(csvValue)"
                      display="chip"
                      filter
                      placeholder="Selecteer hard skill(s)"
                      style="min-width: 20rem"
                      @update:model-value="(v) => setValueMap('hardSkillValueMap', csvValue, v as string[])"
                    />
                  </div>
                  <div class="flex items-center gap-2 pl-1">
                    <Checkbox
                      :input-id="`skip-hs-${csvValue}`"
                      :model-value="isHardSkillSkipped(csvValue)"
                      binary
                      @update:model-value="(v) => setHardSkillSkip(csvValue, !!v)"
                    />
                    <label :for="`skip-hs-${csvValue}`" class="text-xs text-surface-500">
                      Niet importeren — laat deze waarde weg bij elke persona
                    </label>
                  </div>
                </div>
              </div>

              <div v-if="missingCertificates.length" class="flex flex-col gap-2">
                <h3 class="m-0 text-sm font-semibold">
                  Certificaten ({{ missingCertificates.length }})
                </h3>
                <div
                  v-for="csvValue in missingCertificates"
                  :key="`cert-${csvValue}`"
                  class="flex flex-col gap-2 border border-surface-200 rounded-lg p-3"
                >
                  <div class="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
                    <div class="flex flex-1 items-center gap-2">
                      <Tag
                        v-if="isCertificateSkipped(csvValue)"
                        value="overgeslagen"
                        severity="secondary"
                      />
                      <Tag
                        v-else
                        :value="(valueMaps.certificateValueMap[csvValue]?.length ?? 0) > 0 ? 'gemapt' : 'ontbreekt'"
                        :severity="(valueMaps.certificateValueMap[csvValue]?.length ?? 0) > 0 ? 'info' : 'warn'"
                      />
                      <span class="break-all text-sm font-mono">{{ csvValue }}</span>
                    </div>
                    <MultiSelect
                      :model-value="getValueMap(valueMaps.certificateValueMap, csvValue)"
                      :options="dbCertificateNames"
                      :loading="isLoadingOptions"
                      :disabled="isCertificateSkipped(csvValue)"
                      display="chip"
                      filter
                      placeholder="Selecteer certificaat / certificaten"
                      style="min-width: 20rem"
                      @update:model-value="(v) => setValueMap('certificateValueMap', csvValue, v as string[])"
                    />
                  </div>
                  <div class="flex items-center gap-2 pl-1">
                    <Checkbox
                      :input-id="`skip-cert-${csvValue}`"
                      :model-value="isCertificateSkipped(csvValue)"
                      binary
                      @update:model-value="(v) => setCertificateSkip(csvValue, !!v)"
                    />
                    <label :for="`skip-cert-${csvValue}`" class="text-xs text-surface-500">
                      Niet importeren — laat deze waarde weg bij elke persona
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Card>
          <template #title>
            <span>3. Defaults &amp; verdeling</span>
          </template>
          <template #content>
            <div class="flex flex-col gap-6">
              <p class="m-0 text-sm text-surface-500">
                Defaults worden alleen toegepast voor velden die niet aan een kolom zijn gemapt
                óf wanneer een cel leeg is. Wanneer meerdere opties geselecteerd zijn,
                wordt er per rij gewogen-willekeurig gekozen (seed = email-hash).
              </p>

              <!-- Education levels -->
              <div class="flex flex-col gap-2">
                <label for="def-edu" class="text-sm font-semibold">Opleidingsniveau (defaults)</label>
                <MultiSelect
                  input-id="def-edu"
                  :model-value="mapping.defaults.educationLevels.map(o => o.value)"
                  :options="options?.educationLevels ?? []"
                  :loading="isLoadingOptions"
                  display="chip"
                  filter
                  placeholder="Kies één of meer opties"
                  @update:model-value="(v) => mapping.defaults.educationLevels = setDistribution(mapping.defaults.educationLevels, v as string[], stringEq)"
                />
                <div v-if="mapping.defaults.educationLevels.length" class="flex flex-wrap gap-2 pt-2">
                  <div
                    v-for="(item, idx) in mapping.defaults.educationLevels"
                    :key="item.value"
                    class="flex items-center gap-2 border border-surface-200 rounded-lg bg-surface-50 px-2 py-1"
                  >
                    <span class="text-xs font-medium">{{ item.value }}</span>
                    <InputNumber
                      :model-value="item.weight"
                      :min="0"
                      :step="1"
                      show-buttons
                      button-layout="horizontal"
                      :input-style="{ width: '3rem', textAlign: 'center' }"
                      @update:model-value="(v) => mapping.defaults.educationLevels[idx].weight = (v ?? 0)"
                    />
                    <span class="text-xs text-surface-500">
                      {{ percentage(mapping.defaults.educationLevels, item) }}%
                    </span>
                  </div>
                </div>
              </div>

              <!-- Cities -->
              <div class="flex flex-col gap-2">
                <label for="def-city" class="text-sm font-semibold">Stad (defaults)</label>
                <MultiSelect
                  input-id="def-city"
                  :model-value="mapping.defaults.cities.map(o => o.value)"
                  :options="options?.jobCategories ? (options.cities ?? []) : []"
                  :loading="isLoadingOptions"
                  display="chip"
                  filter
                  placeholder="Kies één of meer steden"
                  @update:model-value="(v) => mapping.defaults.cities = setDistribution(mapping.defaults.cities, v as string[], stringEq)"
                />
                <div v-if="mapping.defaults.cities.length" class="flex flex-wrap gap-2 pt-2">
                  <div
                    v-for="(item, idx) in mapping.defaults.cities"
                    :key="item.value"
                    class="flex items-center gap-2 border border-surface-200 rounded-lg bg-surface-50 px-2 py-1"
                  >
                    <span class="text-xs font-medium">{{ item.value }}</span>
                    <InputNumber
                      :model-value="item.weight"
                      :min="0"
                      :step="1"
                      show-buttons
                      button-layout="horizontal"
                      :input-style="{ width: '3rem', textAlign: 'center' }"
                      @update:model-value="(v) => mapping.defaults.cities[idx].weight = (v ?? 0)"
                    />
                    <span class="text-xs text-surface-500">
                      {{ percentage(mapping.defaults.cities, item) }}%
                    </span>
                  </div>
                </div>
              </div>

              <!-- Max distance -->
              <div class="flex flex-col gap-2">
                <label for="def-distance" class="text-sm font-semibold">Max afstand (km)</label>
                <InputNumber
                  input-id="def-distance"
                  v-model="mapping.defaults.maxDistanceKm"
                  :min="1"
                  :max="200"
                  show-buttons
                />
              </div>

              <!-- Hours per week -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-semibold">Uren per week (defaults)</label>
                <div class="flex flex-wrap gap-2">
                  <Button
                    v-for="preset in PRESET_HOURS"
                    :key="`${preset.min}-${preset.max}`"
                    :label="`${rangeLabel(preset)} uur`"
                    :severity="mapping.defaults.hoursPerWeek.some(h => hoursEq(h.value, preset)) ? 'primary' : 'secondary'"
                    size="small"
                    @click="() => {
                      const selected = mapping.defaults.hoursPerWeek.some(h => hoursEq(h.value, preset))
                        ? mapping.defaults.hoursPerWeek.filter(h => !hoursEq(h.value, preset)).map(h => h.value)
                        : [...mapping.defaults.hoursPerWeek.map(h => h.value), preset]
                      mapping.defaults.hoursPerWeek = setDistribution(mapping.defaults.hoursPerWeek, selected, hoursEq)
                    }"
                  />
                </div>
                <div v-if="mapping.defaults.hoursPerWeek.length" class="flex flex-wrap gap-2 pt-2">
                  <div
                    v-for="(item, idx) in mapping.defaults.hoursPerWeek"
                    :key="`${item.value.min}-${item.value.max}`"
                    class="flex items-center gap-2 border border-surface-200 rounded-lg bg-surface-50 px-2 py-1"
                  >
                    <span class="text-xs font-medium">{{ rangeLabel(item.value) }}</span>
                    <InputNumber
                      :model-value="item.weight"
                      :min="0"
                      :step="1"
                      show-buttons
                      button-layout="horizontal"
                      :input-style="{ width: '3rem', textAlign: 'center' }"
                      @update:model-value="(v) => mapping.defaults.hoursPerWeek[idx].weight = (v ?? 0)"
                    />
                    <span class="text-xs text-surface-500">
                      {{ percentage(mapping.defaults.hoursPerWeek, item) }}%
                    </span>
                  </div>
                </div>
              </div>

              <!-- Years experience -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-semibold">Werkervaring in jaren (defaults)</label>
                <div class="flex flex-wrap gap-2">
                  <Button
                    v-for="preset in PRESET_YEARS"
                    :key="`${preset.min}-${preset.max}`"
                    :label="`${rangeLabel(preset)} jaar`"
                    :severity="mapping.defaults.yearsExperience.some(y => yearsEq(y.value, preset)) ? 'primary' : 'secondary'"
                    size="small"
                    @click="() => {
                      const selected = mapping.defaults.yearsExperience.some(y => yearsEq(y.value, preset))
                        ? mapping.defaults.yearsExperience.filter(y => !yearsEq(y.value, preset)).map(y => y.value)
                        : [...mapping.defaults.yearsExperience.map(y => y.value), preset]
                      mapping.defaults.yearsExperience = setDistribution(mapping.defaults.yearsExperience, selected, yearsEq)
                    }"
                  />
                </div>
                <div v-if="mapping.defaults.yearsExperience.length" class="flex flex-wrap gap-2 pt-2">
                  <div
                    v-for="(item, idx) in mapping.defaults.yearsExperience"
                    :key="`${item.value.min}-${item.value.max}`"
                    class="flex items-center gap-2 border border-surface-200 rounded-lg bg-surface-50 px-2 py-1"
                  >
                    <span class="text-xs font-medium">{{ rangeLabel(item.value) }}</span>
                    <InputNumber
                      :model-value="item.weight"
                      :min="0"
                      :step="1"
                      show-buttons
                      button-layout="horizontal"
                      :input-style="{ width: '3rem', textAlign: 'center' }"
                      @update:model-value="(v) => mapping.defaults.yearsExperience[idx].weight = (v ?? 0)"
                    />
                    <span class="text-xs text-surface-500">
                      {{ percentage(mapping.defaults.yearsExperience, item) }}%
                    </span>
                  </div>
                </div>
              </div>

              <!-- Age range -->
              <div class="flex flex-col gap-2">
                <label class="text-sm font-semibold">Leeftijdsrange (voor BirthDate verdeling)</label>
                <div class="flex items-center gap-3">
                  <InputNumber
                    v-model="mapping.defaults.minAge"
                    :min="16"
                    :max="80"
                    show-buttons
                  />
                  <span class="text-surface-500">tot</span>
                  <InputNumber
                    v-model="mapping.defaults.maxAge"
                    :min="mapping.defaults.minAge"
                    :max="80"
                    show-buttons
                  />
                  <span class="text-sm text-surface-500">jaar</span>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Card>
          <template #title>
            <span>4. Match-key voor bestaande users</span>
          </template>
          <template #content>
            <div class="flex flex-col gap-2">
              <p class="m-0 text-sm text-surface-500">
                Veld waarop bestaande users worden gematcht. Als &gt;1 match → fout in resultaat.
              </p>
              <div class="flex gap-4">
                <div
                  v-for="opt in uniqueFieldOptions"
                  :key="opt.value"
                  class="flex items-center gap-2"
                >
                  <RadioButton
                    v-model="mapping.uniqueMatchField"
                    :input-id="`uniq-${opt.value}`"
                    :value="opt.value"
                  />
                  <label :for="`uniq-${opt.value}`" class="text-sm">{{ opt.label }}</label>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Message
          v-if="validationErrors.length"
          severity="warn"
          :closable="false"
        >
          <div class="flex flex-col gap-1">
            <strong>De import kan nog niet starten:</strong>
            <ul class="m-0 pl-5">
              <li v-for="(err, idx) in validationErrors" :key="idx" class="text-sm">
                {{ err }}
              </li>
            </ul>
          </div>
        </Message>

        <div class="flex justify-end">
          <Button
            v-permission="{ resource: AdminPermissionResource.PersonaImport, level: PermissionLevel.Write }"
            label="Importeer personas"
            icon="fa-regular fa-upload"
            :disabled="!canImport"
            severity="primary"
            @click="submitImport"
          />
        </div>
      </template>
    </template>
  </div>
</template>
